/**
 * Button — Triggers actions or events. The most common interactive element.
 *
 * Button Component Guidelines
 * - Choose `variant` based on visual hierarchy: `primary` for the main action on
 *   a page/modal, `secondary` for supporting actions, `tertiary` for low-emphasis
 *   actions (toolbars, inline), `destructive` for irreversible actions, and `link`
 *   for inline text navigation.
 * - Use `size="sm"` (24px) in dense areas like toolbars, table rows, or popovers;
 *   `size="base"` (32px) for standard forms and dialogs; `size="lg"` (40px) for
 *   hero CTAs or standalone page actions.
 * - Set `icon` when the button contains only an icon — it makes the button square.
 *   Pair icon-only buttons with a Tooltip so the action is still discoverable.
 * - Use `loading` during async operations — it disables the button and swaps the
 *   label for a spinner, so the same button can represent both idle and pending states.
 * - Use `startEnhancer` for prefix/leading icons and `endEnhancer` for
 *   suffix/trailing icons next to text. Don't place icons directly in `children`
 *   alongside text — the enhancer slots handle spacing and sizing automatically.
 * @see https://base-ui.com/react/components/button Base UI Button documentation
 * @see https://ui.shadcn.com/docs/components/button shadcn/ui Button documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

const buttonVariants = cva(
  "relative w-fit focus-visible:ring-ring/50 focus-visible:ring-3 aria-invalid:ring-rose-border aria-invalid:border-rose-border rounded-lg border border-0.5 border-transparent bg-clip-padding aria-invalid:ring-[3px] inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none gap-2 shadow-2xs transition-opacity transition-colors duration-150",
  {
    variants: {
      variant: {
        primary:
          "bg-button-primary text-on-button-primary hover:bg-button-primary-hover focus-visible:border-ring",
        secondary:
          "border-border bg-button-secondary text-on-button-secondary hover:bg-button-secondary-hover hover:text-on-button-secondary-hover aria-expanded:bg-button-secondary-hover aria-expanded:text-on-button-secondary-hover",
        destructive:
          "border-rose-border bg-rose-soft hover:text-white hover:bg-button-danger-hover text-rose shadow-2xs focus-visible:ring-rose-border",
        tertiary:
          "text-default hover:bg-button-tertiary-hover aria-expanded:bg-button-tertiary-hover shadow-none",
        link: "text-default underline-offset-4 hover:underline shadow-none px-0! h-fit! font-medium",
      },
      size: {
        sm: "text-xs [&_svg:not([class*='size-'])]:size-3 rounded-md",
        base: "text-sm [&_svg:not([class*='size-'])]:size-3.5",
        lg: "text-base [&_svg:not([class*='size-'])]:size-4",
      },
      icon: {
        true: "",
        false: "",
      },
      align: {
        center: "justify-center",
        start: "justify-start",
      },
      allowShrink: {
        true: "min-w-0 shrink",
        false: "shrink-0",
      },
    },
    compoundVariants: [
      { icon: false, size: "sm", className: "h-6 px-2" },
      { icon: false, size: "base", className: "h-8 px-2.5" },
      { icon: false, size: "lg", className: "h-10 px-3" },
      { icon: true, size: "sm", className: "size-6 p-2" },
      { icon: true, size: "base", className: "size-8 p-2.5" },
      { icon: true, size: "lg", className: "size-10 p-3" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "base",
      icon: false,
      align: "center",
      allowShrink: false,
    },
  },
);

interface ButtonProps<ClassNameType extends string = string>
  extends Omit<ButtonPrimitive.Props, "className">,
    VariantProps<typeof buttonVariants> {
  /**
   * The native button type attribute. Omit collapses the Base UI discriminated
   * union (ButtonNativeProps | ButtonNonNativeProps), dropping this prop — so we
   * re-add it explicitly.
   */
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  /** Prefix/leading content, usually an icon, rendered inline before the label. */
  startEnhancer?: React.ReactNode;
  /** Suffix/trailing content, usually an icon, rendered inline after the label. */
  endEnhancer?: React.ReactNode;
  truncate?: boolean;
  align?: "center" | "start";
  allowShrink?: boolean;
  /**
   * className with type safety for layout classes only
   */
  className?: LayoutClassName<ClassNameType>;
  /**
   * flexible className marked as UNSAFE to indicate it is circumventing the design system
   */
  UNSAFE_className?: string;
}

// A leading/trailing icon reads as part of the button's padding, so a button
// with an enhancer on one side looks lopsided against the symmetric px-* above.
// Tighten the enhancer side to optically re-center the icon + label cluster.
const enhancerPaddingBySize: Record<
  NonNullable<ButtonProps["size"]>,
  { start: string; end: string }
> = {
  sm: { start: "pl-1.5", end: "pr-1.5" },
  base: { start: "pl-2", end: "pr-2" },
  lg: { start: "pl-2.5", end: "pr-2.5" },
};

const ButtonInner = <ClassName extends string>(
  {
    variant = "primary",
    size = "base",
    icon = false,
    loading,
    startEnhancer,
    endEnhancer,
    truncate = false,
    align = "center",
    allowShrink = false,
    className,
    UNSAFE_className,
    ...props
  }: ButtonProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const enhancerPaddingForSize = enhancerPaddingBySize[size ?? "base"];
  const enhancerPadding =
    !icon && variant !== "link"
      ? cn(
          startEnhancer && enhancerPaddingForSize.start,
          endEnhancer && enhancerPaddingForSize.end,
        )
      : undefined;

  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      disabled={props.disabled || loading}
      className={cn(
        buttonVariants({
          variant,
          size,
          icon,
          align,
          allowShrink,
        }),
        enhancerPadding,
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {!icon && startEnhancer && (
        <div
          className={cn(
            "transition-opacity duration-150",
            loading && "opacity-0",
          )}
        >
          {startEnhancer}
        </div>
      )}

      {props.children && (
        <span
          className={cn(
            "transition-opacity duration-150",
            icon && "flex items-center justify-center",
            truncate && "min-w-0 flex-1 truncate",
            loading && "opacity-0",
          )}
        >
          {props.children}
        </span>
      )}

      <LoaderCircle
        className={cn(
          "absolute transition-opacity duration-150",
          loading ? "animate-spin opacity-100" : "opacity-0",
        )}
      />

      {!icon && endEnhancer && (
        <span
          className={cn(
            "transition-opacity duration-150",
            loading && "opacity-0",
          )}
        >
          {endEnhancer}
        </span>
      )}
    </ButtonPrimitive>
  );
};

// NOTE (Ben, 01/13/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const Button = React.forwardRef(ButtonInner) as <ClassName extends string>(
  props: ButtonProps<ClassName> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

export { Button };
